import React from 'react';
import { X, Settings, FileType, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExportConfig } from './ExportConfigPanel';
import { format } from 'date-fns';

interface ExportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: ExportConfig;
  data: any[];
  onAdjustColumns: () => void;
  onChangeFormat: () => void;
  onProceedExport: () => void;
}

export function ExportPreviewModal({ 
  isOpen, 
  onClose, 
  config, 
  data, 
  onAdjustColumns, 
  onChangeFormat, 
  onProceedExport 
}: ExportPreviewModalProps) {
  const previewData = data.slice(0, 10);
  const totalRecords = data.length;
  
  const formatCellValue = (value: any, columnKey: string) => {
    if (value === null || value === undefined) return '-';
    
    switch (columnKey) {
      case 'submitted_at':
        return format(new Date(value), 'MM/dd/yyyy HH:mm');
      case 'risk_score':
        return `${value}%`;
      case 'saved_amount':
        return `$${value.toFixed(2)}`;
      case 'detected_patterns':
        return Array.isArray(value) ? value.join(', ') : value;
      case 'status':
        return (
          <Badge variant={
            value === 'blocked' ? 'destructive' :
            value === 'approved' ? 'default' :
            'secondary'
          }>
            {value}
          </Badge>
        );
      default:
        return String(value);
    }
  };

  const getColumnLabel = (key: string) => {
    const columnMap: Record<string, string> = {
      id: 'ID',
      submitted_at: 'Timestamp',
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      ip_address: 'IP Address',
      zip_code: 'Zip Code',
      risk_score: 'Risk Score',
      detected_patterns: 'Patterns',
      status: 'Status',
      source: 'Source',
      user_agent: 'User Agent',
      saved_amount: 'Amount Saved'
    };
    return columnMap[key] || key;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Export Preview</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Showing first 10 rows of {totalRecords} total records
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {config.format.toUpperCase()}
              </Badge>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Export Configuration Summary */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Format:</span> {config.format.toUpperCase()}
            </div>
            <div>
              <span className="font-medium">Records:</span> {totalRecords}
            </div>
            <div>
              <span className="font-medium">Columns:</span> {config.columns.length}
            </div>
            <div>
              <span className="font-medium">Date Range:</span>{' '}
              {config.dateRange.from ? format(config.dateRange.from, 'MM/dd/yyyy') : 'All'} -{' '}
              {config.dateRange.to ? format(config.dateRange.to, 'MM/dd/yyyy') : 'All'}
            </div>
          </div>
          <div className="text-sm">
            <span className="font-medium">Filters:</span>{' '}
            {config.dataTypes.length > 0 ? config.dataTypes.join(', ') : 'All types'}{' '}
            {config.patterns.length > 0 && `• Patterns: ${config.patterns.join(', ')}`}
          </div>
        </div>

        {/* Preview Table */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <Table>
              <TableHeader>
                <TableRow>
                  {config.columns.map((columnKey) => (
                    <TableHead key={columnKey} className="whitespace-nowrap">
                      {getColumnLabel(columnKey)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewData.map((row, index) => (
                  <TableRow key={row.id || index}>
                    {config.columns.map((columnKey) => (
                      <TableCell key={columnKey} className="max-w-[200px] truncate">
                        {formatCellValue(row[columnKey], columnKey)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* Preview Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Preview shows first 10 of {totalRecords} records
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onAdjustColumns}>
              <Settings className="h-4 w-4 mr-2" />
              Adjust Columns
            </Button>
            <Button variant="outline" onClick={onChangeFormat}>
              <FileType className="h-4 w-4 mr-2" />
              Change Format
            </Button>
            <Button onClick={onProceedExport}>
              <Download className="h-4 w-4 mr-2" />
              Proceed with Export
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}